/**
 * 수동 객체 생성 시 자동 이름 생성 및 convention layer 해결
 */

import { dia } from '@joint/core'
import { DEFAULT_PREFIXES } from '../constants/idPrefixes'
import type { LayerTypeMapping } from '../store/objectTypeStore/types'

/**
 * ObjectType의 UUID typeId에서 convention-based layer name을 역추적한다.
 * LayerTypeMapping에서 typeId → layerName 매핑을 찾는다.
 *
 * @returns convention layer name (예: 'p-parking-basic') 또는 null
 */
export function resolveConventionLayer(
  typeId: string,
  layerTypeMappings: LayerTypeMapping[]
): string | null {
  const mapping = layerTypeMappings.find(m => m.typeId === typeId)
  return mapping?.layerName || null
}

/**
 * convention layer name 또는 typeId에서 prefix를 조회한다.
 * 우선순위: conventionLayer → typeId → 'XX' fallback
 */
export function getPrefix(conventionLayer: string | null, typeId?: string): string {
  if (conventionLayer && DEFAULT_PREFIXES[conventionLayer]) {
    return DEFAULT_PREFIXES[conventionLayer]
  }
  if (typeId && DEFAULT_PREFIXES[typeId]) {
    return DEFAULT_PREFIXES[typeId]
  }
  return 'XX'
}

/**
 * graph 내 동일 prefix를 가진 객체들의 최대 번호를 찾아
 * {prefix}-{max+1} 형식의 이름을 반환한다.
 *
 * @param typeId - ObjectType의 id (UUID)
 * @param graph - JointJS graph 인스턴스
 * @param layerTypeMappings - 현재 floor의 LayerTypeMapping 목록
 * @param layerHint - 호출자가 이미 알고 있는 canonical layer key
 *   (예: useObjectCreation 의 DISPLAY_NAME_TO_LAYER_KEY 결과). 주어지면 가장
 *   먼저 DEFAULT_PREFIXES 에서 lookup 한다. CSV layer-mapping 흐름은 nil 로
 *   호출하면 기존 conventionLayer / typeId fallback 그대로.
 * @param fallbackPrefix - DEFAULT_PREFIXES 에 매칭이 전혀 없을 때('XX' 폴백
 *   상황) 대신 사용할 prefix. 사용자 정의 ObjectType 의 경우 타입명을 넘겨
 *   "타입명-N" 형태로 객체명이 생성되게 한다.
 * @returns 생성된 이름 (예: 'PB-1', 'PB-2', 'lightCctv-1', 'EXIT-1')
 */
export function generateObjectName(
  typeId: string,
  graph: dia.Graph,
  layerTypeMappings: LayerTypeMapping[] = [],
  layerHint?: string | null,
  fallbackPrefix?: string,
): string {
  const conventionLayer = resolveConventionLayer(typeId, layerTypeMappings)
  // layerHint 가 prefix 매핑에 있다면 최우선. 없으면 기존 resolve 결과로 폴백.
  let prefix =
    (layerHint && DEFAULT_PREFIXES[layerHint]) ||
    getPrefix(conventionLayer, typeId)
  if (prefix === 'XX' && fallbackPrefix) {
    prefix = fallbackPrefix
  }
  const pattern = `${prefix}-`

  let maxNumber = 0

  graph.getElements().forEach(element => {
    const data = element.get('data')
    const name: string = data?.properties?.name || ''
    if (name.startsWith(pattern)) {
      const num = parseInt(name.slice(pattern.length), 10)
      if (!isNaN(num) && num > maxNumber) {
        maxNumber = num
      }
    }
  })

  return `${prefix}-${maxNumber + 1}`
}
